import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    inquiryType: '',
    subject: '',
    message: '',
    preferredContact: 'email',
    urgency: 'normal'
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const inquiryTypes = [
    { value: 'membership', label: 'Membership Inquiry', icon: 'UserPlus', description: 'Questions about joining our club' },
    { value: 'partnership', label: 'Partnership Opportunity', icon: 'Handshake', description: 'Corporate or organizational partnerships' },
    { value: 'volunteer', label: 'Volunteer Opportunity', icon: 'Heart', description: 'Non-member volunteer participation' },
    { value: 'donation', label: 'Donation & Support', icon: 'Gift', description: 'Financial support and sponsorship' },
    { value: 'media', label: 'Media & Press', icon: 'Camera', description: 'Media inquiries and press coverage' },
    { value: 'general', label: 'General Information', icon: 'Info', description: 'Other questions and information' }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    try {
      await new Promise(resolve => setTimeout(resolve, 2000));
      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        phone: '',
        inquiryType: '',
        subject: '',
        message: '',
        preferredContact: 'email',
        urgency: 'normal'
      });
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getInquiryTypeDetails = (type) => {
    return inquiryTypes.find(inquiry => inquiry.value === type);
  };

  return (
    <section className="py-16 lg:py-24 bg-surface">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
            Send Us a Message
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Our team will route your message to the right person and respond within 24 hours
          </p>
        </div>

        <div className="bg-card rounded-xl p-8 lg:p-12 card-shadow">
          {submitStatus === 'success' && (
            <div className="mb-8 p-4 bg-success/10 border border-success/20 rounded-lg">
              <div className="flex items-center gap-3">
                <Icon name="CheckCircle" size={24} className="text-success" />
                <div>
                  <h3 className="font-semibold text-success">Message Sent Successfully!</h3>
                  <p className="text-sm text-success/80">We'll get back to you within 24 hours.</p>
                </div>
              </div>
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="mb-8 p-4 bg-error/10 border border-error/20 rounded-lg">
              <div className="flex items-center gap-3">
                <Icon name="AlertCircle" size={24} className="text-error" />
                <div>
                  <h3 className="font-semibold text-error">Message Failed to Send</h3>
                  <p className="text-sm text-error/80">Please try again or contact us directly.</p>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Personal Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-card-foreground mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-smooth"
                  placeholder="Enter your full name"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-card-foreground mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-smooth"
                  placeholder="your.email@example.com"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-card-foreground mb-2">
                  Phone Number
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-smooth"
                  placeholder="+94 77 123 4567"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-card-foreground mb-2">
                  Preferred Contact Method
                </label>
                <select
                  name="preferredContact"
                  value={formData.preferredContact}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-smooth"
                >
                  <option value="email">Email</option>
                  <option value="phone">Phone Call</option>
                  <option value="whatsapp">WhatsApp</option>
                </select>
              </div>
            </div>

            {/* Inquiry Type */}
            <div>
              <label className="block text-sm font-medium text-card-foreground mb-4">
                Type of Inquiry *
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {inquiryTypes.map((inquiry) => (
                  <label
                    key={inquiry.value}
                    className={`relative flex items-start gap-3 p-4 border rounded-lg cursor-pointer transition-smooth ${
                      formData.inquiryType === inquiry.value
                        ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="inquiryType"
                      value={inquiry.value}
                      checked={formData.inquiryType === inquiry.value}
                      onChange={handleInputChange}
                      className="sr-only"
                    />
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      formData.inquiryType === inquiry.value ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'
                    }`}>
                      <Icon name={inquiry.icon} size={16} />
                    </div>
                    <div>
                      <p className="font-medium text-card-foreground text-sm">
                        {inquiry.label}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {inquiry.description}
                      </p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Subject and Message */}
            <div>
              <label className="block text-sm font-medium text-card-foreground mb-2">
                Subject *
              </label>
              <input
                type="text"
                name="subject"
                value={formData.subject}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-smooth"
                placeholder="Brief description of your inquiry"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-card-foreground mb-2">
                Message *
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                required
                rows={6}
                className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-smooth resize-none"
                placeholder="Please provide details about your inquiry, questions, or how we can help you..."
              />
              <p className="text-xs text-muted-foreground mt-2">
                Minimum 50 characters. Be specific to help us provide the best response.
              </p>
            </div>

            {/* Urgency Level */}
            <div>
              <label className="block text-sm font-medium text-card-foreground mb-2">
                Urgency Level
              </label>
              <div className="flex gap-4">
                {[
                  { value: 'low', label: 'Low Priority', color: 'text-muted-foreground' },
                  { value: 'normal', label: 'Normal', color: 'text-primary' },
                  { value: 'high', label: 'High Priority', color: 'text-warning' },
                  { value: 'urgent', label: 'Urgent', color: 'text-error' }
                ].map((urgency) => (
                  <label key={urgency.value} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="urgency"
                      value={urgency.value}
                      checked={formData.urgency === urgency.value}
                      onChange={handleInputChange}
                      className="w-4 h-4 text-primary border-border focus:ring-primary"
                    />
                    <span className={`text-sm ${urgency.color}`}>
                      {urgency.label}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Submit Button */}
            <div className="pt-6 border-t border-border">
              <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
                <div className="text-sm text-muted-foreground">
                  <Icon name="Shield" size={16} className="inline mr-2" />
                  Your information is secure and will not be shared with third parties.
                </div>
                
                <Button
                  type="submit"
                  variant="default"
                  size="lg"
                  loading={isSubmitting}
                  iconName="Send"
                  iconPosition="left"
                  disabled={!formData.name || !formData.email || !formData.inquiryType || !formData.subject || !formData.message}
                >
                  {isSubmitting ? 'Sending Message...' : 'Send Message'}
                </Button>
              </div>
            </div>
          </form>

          {/* Response Time Information */}
          <div className="mt-8 pt-8 border-t border-border">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
              <div className="flex flex-col items-center gap-2">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                  <Icon name="Clock" size={24} className="text-primary" />
                </div>
                <h4 className="font-semibold text-card-foreground">Quick Response</h4>
                <p className="text-sm text-muted-foreground">Within 24 hours on weekdays</p>
              </div>
              
              <div className="flex flex-col items-center gap-2">
                <div className="w-12 h-12 bg-secondary/10 rounded-full flex items-center justify-center">
                  <Icon name="Users" size={24} className="text-secondary" />
                </div>
                <h4 className="font-semibold text-card-foreground">Right Person</h4>
                <p className="text-sm text-muted-foreground">Routed to appropriate officer</p>
              </div>
              
              <div className="flex flex-col items-center gap-2">
                <div className="w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center">
                  <Icon name="CheckCircle" size={24} className="text-accent" />
                </div>
                <h4 className="font-semibold text-card-foreground">Follow-up</h4>
                <p className="text-sm text-muted-foreground">Continued support until resolved</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;